// Hindi of src/content/learn/conditions.ts. Keep keys and structure in step with the English.
import type { ArticleBody } from './types';

const NHS = (path: string, label: string) => ({ label: `NHS, ${label}`, href: `https://www.nhs.uk/${path}` });
const AAP = (path: string, label: string) => ({ label: `American Academy of Pediatrics (HealthyChildren.org) ${label}`, href: `https://www.healthychildren.org/English/${path}` });
const RBSK = { label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' };

const DEV_PAED = 'बच्चों के विकास के विशेषज्ञ डॉक्टर (डेवलपमेंटल पीडियाट्रिशियन)';

export const CONDITIONS_HI: Record<string, ArticleBody> = {
  autism: {
    intro: [
      'ऑटिज़्म जीवन भर रहने वाला एक अंतर है, इसमें कि व्यक्ति कैसे बातचीत करता है, दूसरों से कैसे जुड़ता है और दुनिया को कैसे महसूस करता है। इसे **स्पेक्ट्रम** कहा जाता है क्योंकि यह हर बच्चे में बहुत अलग दिखता है: कुछ बच्चे बहुत बोलते हैं और कुछ बहुत कम या बिल्कुल भी शब्द नहीं बोलते; कुछ को बहुत ज़्यादा सहारे की ज़रूरत होती है और कुछ को थोड़े की।',
      'ऑटिज़्म परवरिश, स्क्रीन या टीकों की वजह से नहीं होता। बच्चे जन्म से ही ऑटिस्टिक होते हैं; इसके संकेत आमतौर पर शुरुआती कुछ सालों में ज़्यादा साफ़ दिखने लगते हैं।',
    ],
    notice: [
      'लगभग पहले जन्मदिन तक अपना नाम पुकारे जाने पर जवाब न देना, जबकि सुनने में कोई दिक्कत नहीं है।',
      'इशारे कम करना, जैसे उँगली से दिखाना, हाथ हिलाकर टाटा करना या आपको चीज़ें दिखाना; आपस में मुस्कुराने या चीज़ें साझा करने का लेन-देन कम होना।',
      'देर से बोलना, पहले बोले जाने वाले शब्द छूट जाना, या वीडियो या दूसरे लोगों के वाक्य दोहराना।',
      'ख़ास तरीक़ों से खेलना, खिलौनों को लाइन में लगाना, पहिए घुमाना, चीज़ों को हिलते हुए देखते रहना, और बीच में टोके जाने पर बहुत मुश्किल होना।',
      'आवाज़ों, छूने के एहसास (टेक्सचर), रोशनी या खाने पर तेज़ प्रतिक्रिया, या दर्द या ठंड का जैसे पता ही न चलना।',
      'दिनचर्या, रास्ते या नियमों में छोटे बदलाव पर भी बहुत ज़्यादा परेशान हो जाना।',
      'कई बच्चे प्यार जताने वाले, मज़ेदार भी होते हैं और अपनी पसंद की चीज़ों में गहरी दिलचस्पी रखते हैं। दोनों बातें सच हैं।',
    ],
    helps: [
      'खेल में उनके पीछे चलें। वे जो पहले से कर रहे हैं, उसमें शामिल हों और एक छोटी-सी नई चीज़ जोड़ें।',
      'कम शब्द बोलें और ज़्यादा करके दिखाएँ: उँगली से दिखाएँ, इशारे और तस्वीरें इस्तेमाल करें, और दिन के लिए तस्वीरों वाली समय-सारणी (विज़ुअल टाइमटेबल) रखें।',
      'दिनचर्या तय रखें और बदलाव से पहले बता दें (“दो मिनट और, फिर नहाना”)।',
      'ध्यान दें कि उनकी इंद्रियों को क्या चाहिए, एक शांत कोना, हेडफ़ोन, कसकर गले लगाना, हिलने-डुलने का समय, और उसके हिसाब से योजना बनाएँ।',
      'बातचीत के हर तरीक़े की सराहना करें, जिसमें इशारे, तस्वीरें और डिवाइस भी शामिल हैं। ये बोलना शुरू होने से नहीं रोकते; अक्सर इसमें मदद करते हैं।',
    ],
    myths: [
      { myth: 'घर में दो भाषाएँ बच्चों को उलझा देती हैं और ऑटिज़्म या देर से बोलने की वजह बनती हैं।', fact: 'दो भाषाओं वाले घर ऑटिज़्म या देरी की वजह नहीं बनते। वह भाषा बोलें जिसमें आप सबसे ज़्यादा सहज हैं।' },
      { myth: 'ऑटिज़्म बहुत ज़्यादा टीवी देखने या रूखी परवरिश से होता है।', fact: 'इनमें से किसी से भी ऑटिज़्म नहीं होता। ऑटिज़्म दिमाग़ के विकास के तरीक़े में एक अंतर है।' },
      { myth: 'ऑटिस्टिक बच्चे प्यार महसूस नहीं करते या दोस्त नहीं चाहते।', fact: 'वे गहराई से प्यार करते हैं और जुड़ते हैं, कभी-कभी ऐसे तरीक़ों से जो हमारी उम्मीद से अलग दिखते हैं।' },
    ],
    when: [
      '12 महीने तक बड़बड़ाना (बैबलिंग) या उँगली से दिखाना नहीं, 16 महीने तक एक भी शब्द नहीं, या 24 महीने तक दो शब्दों वाले वाक्य नहीं।',
      'किसी भी उम्र में, आपके बच्चे के पहले के शब्दों या सामाजिक कौशलों में से किसी का भी छूट जाना।',
      'आपको, किसी टीचर या रिश्तेदार को बार-बार लगता है कि कुछ अलग है। उस एहसास की जाँच करवाना सही है।',
    ],
    who: [DEV_PAED, 'बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)', 'स्पीच और लैंग्वेज थेरेपिस्ट', 'ऑक्युपेशनल थेरेपिस्ट'],
    nurture:
      'घरेलू आकलन जुड़ने और बातचीत को ध्यान से देखता है, और रिपोर्ट आसान शब्दों में बताती है कि क्या ख़ास दिखा, साथ में एक पेज जो आप डॉक्टर को दे सकते हैं। यह ऑटिज़्म का निदान नहीं कर सकता; यह आपको जल्दी सही सवाल पूछने में मदद करता है।',
    sources: [
      { label: 'World Health Organization; Autism', href: 'https://www.who.int/news-room/fact-sheets/detail/autism-spectrum-disorders' },
      NHS('conditions/autism/', 'Autism'),
      AAP('health-issues/conditions/Autism/Pages/default.aspx', 'Autism'),
    ],
  },

  adhd: {
    intro: [
      'ADHD (अटेंशन डेफ़िसिट हाइपरएक्टिविटी डिसऑर्डर) इस बात में एक अंतर है कि दिमाग़ ध्यान, सक्रियता और आवेग (बिना सोचे कुछ कर बैठना) को कैसे सँभालता है। हर बच्चा कभी-कभी बेचैन होता है या उसका ध्यान भटकता है; ADHD में ऐसा उसी उम्र के दूसरे बच्चों से कहीं ज़्यादा होता है, एक से ज़्यादा जगह होता है, और रोज़मर्रा की ज़िंदगी में रुकावट बनता है।',
      'ADHD ख़राब परवरिश या चीनी की वजह से नहीं होता। यह अक्सर परिवारों में चलता है।',
    ],
    notice: [
      'खाने, पढ़ाई या कहानियों के दौरान बैठे रहना बहुत मुश्किल लगना, भले ही वे उन्हें पसंद हों।',
      'कई काम शुरू करना और कुछ ही पूरे करना; चीज़ें खो देना; निर्देश बीच में ही भूल जाना।',
      'सोचने से पहले कर बैठना: झपट लेना, बीच में बोलना, सड़क पर दौड़ जाना।',
      'पसंद के गेम पर घंटों ध्यान लगा पाना, लेकिन होमवर्क पर नहीं, यह ADHD में आम है, इस बात का संकेत नहीं कि ADHD नहीं है।',
      'बड़ी भावनाएँ जो जल्दी भड़कती हैं और जल्दी शांत भी हो जाती हैं।',
      'कुछ बच्चों में, ख़ासकर लड़कियों में, ADHD ज़्यादातर ख़यालों में खोए रहने और चुपचाप बिखरे-बिखरे रहने के रूप में दिखता है, और आसानी से नज़र से छूट जाता है।',
    ],
    helps: [
      'एक बार में एक ही निर्देश दें, जब आपके बच्चे की नज़र आप पर हो, और उनसे उसे दोहराने को कहें।',
      'कामों को छोटे-छोटे चरणों में बाँटें और टिक लिस्ट बनाएँ; थोड़ी-थोड़ी देर के काम के लिए टाइमर इस्तेमाल करें, बीच में हिलने-डुलने के ब्रेक के साथ।',
      'उन्हें अच्छा करते हुए पकड़ें। ख़ास तारीफ़ (“तुमने पहली बार में ही जूते रख दिए!”) टोकने से बेहतर काम करती है।',
      'हर दिन सुबह और शाम एक जैसी रखें। अगले दिन की तैयारी रात में ही कर लें।',
      'बाहर खूब खेलना, नियमित नींद और समय पर खाना ध्यान में किसी भी एक खाने या सप्लीमेंट से ज़्यादा मदद करते हैं।',
    ],
    myths: [
      { myth: 'फ़ोन पर तो ध्यान लगा लेते हैं, इसलिए ADHD नहीं हो सकता।', fact: 'ADHD वाले बच्चे रोमांचक लगने वाली चीज़ों पर बहुत गहराई से ध्यान (हाइपर-फ़ोकस) लगा सकते हैं। मुश्किल उन कामों में होती है जो रोमांचक नहीं लगते।' },
      { myth: 'ADHD बस शरारत है और इसके लिए ज़्यादा सख़्ती चाहिए।', fact: 'कड़ी सज़ा से आमतौर पर हालात और बिगड़ते हैं। साफ़ दिनचर्या और प्यार भरा, एक जैसा बर्ताव मदद करता है।' },
    ],
    when: [
      'मुश्किलें घर और स्कूल दोनों जगह (या दूसरे देखभाल करने वालों के साथ) छह महीने या उससे ज़्यादा समय से दिख रही हैं।',
      'टीचर भी वही चिंताएँ बता रहे हैं जो आप घर पर देखते हैं।',
      'इसकी वजह से आपके बच्चे को अक्सर चोट लगती है, उन्हें अलग-थलग कर दिया जाता है, या वे ख़ुद से परेशान रहते हैं।',
    ],
    who: [DEV_PAED, 'बाल मनोचिकित्सक (चाइल्ड साइकियाट्रिस्ट)', 'बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)'],
    nurture:
      'लगभग 3 साल और उससे बड़े बच्चों के लिए, घरेलू आकलन घर पर ध्यान और सक्रियता के बारे में पूछता है। रिपोर्ट दिखाती है कि इस क्षेत्र पर ध्यान देने की ज़रूरत है या इस पर नज़र रखते रहना है, साथ में घर के लिए सुझाव और आपके बच्चे के डॉक्टर के लिए एक पेज।',
    sources: [NHS('conditions/attention-deficit-hyperactivity-disorder-adhd/', 'ADHD'), AAP('health-issues/conditions/adhd/Pages/default.aspx', 'ADHD')],
  },

  'learning-difficulties': {
    intro: [
      'विशिष्ट सीखने की कठिनाई (स्पेसिफ़िक लर्निंग डिफ़िकल्टी) का मतलब है कि बच्चे को सीखने का कोई एक क्षेत्र, अक्सर पढ़ना और स्पेलिंग (**डिस्लेक्सिया**), लिखावट (**डिस्ग्राफ़िया**) या गणित (**डिस्कैलकुलिया**) उससे कहीं ज़्यादा मुश्किल लगता है जितना उनकी बाक़ी समझदारी और क़ाबिलियत को देखकर लगना चाहिए।',
      'इसका बुद्धि या मेहनत से कोई लेना-देना नहीं है। सही पढ़ाई और सहारे के साथ, सीखने में कठिनाई वाले बच्चे सीखते हैं, और कई बहुत अच्छा करते हैं।',
    ],
    notice: [
      'सहपाठियों के आगे बढ़ जाने के काफ़ी समय बाद भी अक्षरों और उनकी आवाज़ों में गड़बड़ करना, या तस्वीरें देखकर शब्दों का अंदाज़ा लगाना।',
      'धीरे पढ़ना और जल्दी थक जाना; ख़ुद पढ़ी कहानी से कहीं बेहतर, ज़ोर से पढ़कर सुनाई गई कहानी समझना।',
      'एक ही पेज पर एक ही शब्द की अलग-अलग स्पेलिंग लिखना; बहुत बिखरी हुई लिखावट, या लिखने में दर्द होना।',
      'पहाड़े याद रखने, घड़ी देखकर समय बताने या पैसों का हिसाब करने में मुश्किल।',
      'होमवर्क से नफ़रत, “मैं बेवकूफ़ हूँ” कहना, या स्कूल से पहले पेट दर्द।',
    ],
    helps: [
      'हर दिन साथ मिलकर ज़ोर से पढ़ें, मज़े के लिए, परखने के लिए नहीं। ऑडियोबुक भी गिनी जाती हैं।',
      'अभ्यास छोटा, रोज़ाना और मज़ेदार रखें: चुंबक वाले अक्षर, कविताएँ, पासे और गिनती वाले बोर्ड गेम।',
      'सिर्फ़ नंबरों की नहीं, मेहनत और तरीक़े की भी तारीफ़ करें।',
      'क्लास टीचर से जल्दी बात करें और पूछें कि वे क्लास में क्या मदद दे सकते हैं।',
    ],
    myths: [
      { myth: 'वे आलसी हैं, उन्हें बस ज़्यादा कोशिश करनी है।', fact: 'सीखने में कठिनाई वाले बच्चे अक्सर क्लास में किसी से भी ज़्यादा मेहनत कर रहे होते हैं।' },
      { myth: 'बड़े होकर अपने आप ठीक हो जाएगा।', fact: 'कठिनाई बनी रहती है, लेकिन अच्छी पढ़ाई और सहारे से बड़ा फ़र्क पड़ता है, और जितना जल्दी, उतना बेहतर।' },
    ],
    when: [
      'मदद के बावजूद, स्कूल के एक साल या उससे ज़्यादा के बाद भी पढ़ना, लिखना या गणित साफ़ तौर पर पीछे है।',
      'आपके बच्चे में स्कूल को लेकर घबराहट बढ़ रही है या आत्मविश्वास कम हो रहा है।',
    ],
    who: ['स्पेशल एजुकेटर', 'बाल मनोवैज्ञानिक (सीखने से जुड़े आकलन के लिए)', 'ऑक्युपेशनल थेरेपिस्ट (लिखावट के लिए)', DEV_PAED],
    nurture:
      'Nurture स्कूल के विषयों के बजाय रोज़मर्रा के विकास को देखता है, इसलिए यह पढ़ने या गणित की जाँच नहीं करता। यह दिखा सकता है कि क्या ध्यान, भाषा या दूसरे क्षेत्रों को भी देखना चाहिए, जो सीखने से जुड़े आकलन के लिए पूछते समय साझा करने में काम आता है।',
    sources: [NHS('conditions/dyslexia/', 'Dyslexia'), AAP('health-issues/conditions/learning-disabilities/Pages/default.aspx', 'Learning disabilities')],
  },

  'speech-language-delay': {
    intro: [
      'बोलने और भाषा में देरी का मतलब है कि बच्चे उसी उम्र के ज़्यादातर बच्चों की तुलना में शब्दों को देर से समझ या इस्तेमाल कर पा रहे हैं। **बोली (स्पीच)** का मतलब है आवाज़ें साफ़ निकालना; **भाषा** का मतलब है शब्दों को समझना और उन्हें जोड़कर अपनी बात कहना। किसी बच्चे को इनमें से एक या दोनों में मुश्किल हो सकती है।',
      'कुछ बच्चे अपने आप बराबरी पर आ जाते हैं। दूसरों को मदद चाहिए होती है, और बाहर से यह बताना मुश्किल है कि कौन किसमें आता है, इसलिए इंतज़ार करने के बजाय जाँच करवाना बेहतर है।',
    ],
    notice: [
      '18 महीने तक बहुत कम शब्द, या 2 साल तक दो शब्द जोड़कर न बोलना।',
      'चीज़ें माँगने के लिए शब्द बोलने की कोशिश करने के बजाय उँगली से दिखाना, आपको खींचना या रोना।',
      'बिना इशारों के “अपने जूते लाओ” जैसे आसान निर्देश न मान पाना।',
      'लगभग 3 साल की उम्र तक भी ऐसी बोली जिसे घर से बाहर के रिश्तेदार न समझ पाएँ।',
      'बात समझी न जाने पर झुंझलाहट, ज़िद वाला गुस्सा (टैंट्रम) या हार मान लेना।',
    ],
    helps: [
      'आपका बच्चा जो कुछ करे, उसी समय उसके बारे में बोलते जाएँ: “अच्छा, पानी डालना है! डालो, डालो!”',
      'वे जो कहें उसमें एक शब्द जोड़ें: अगर वे “गाड़ी” कहें, तो आप कहें “लाल गाड़ी” या “गाड़ी चली”।',
      'उनकी आँखों की ऊँचाई तक झुकें और इंतज़ार करें, मन में पाँच तक गिनें, ताकि उन्हें अपनी बारी मिले।',
      'हाव-भाव के साथ कविताएँ गाएँ, तस्वीरों वाली किताबें पढ़ें, और पीछे चल रही स्क्रीन बंद कर दें।',
      'अपनी घर की भाषा बोलें; आपके पास साझा करने के लिए यही सबसे समृद्ध भाषा है।',
    ],
    myths: [
      { myth: 'लड़के तो हमेशा देर से बोलते हैं, रुककर देखिए।', fact: 'औसतन लड़के थोड़ा देर से बोल सकते हैं, लेकिन किसी भी बच्चे में साफ़ देरी हो तो जाँच करवाना सही है।' },
      { myth: 'घर में दो भाषाओं की वजह से बच्चे देर से बोलते हैं।', fact: 'ऐसा नहीं है। दो भाषाएँ बोलने वाले बच्चे, अपनी सभी भाषाओं को मिलाकर देखें तो, सामान्य उम्र-सीमा में ही पड़ाव पूरे करते हैं।' },
    ],
    when: [
      '16 महीने तक कोई शब्द नहीं, 2 साल तक दो शब्दों वाले वाक्य नहीं, या किसी भी उम्र में शब्द छूट जाना।',
      'आपके बच्चे को उनसे कही गई आसान बातें समझ में नहीं आतीं, उनके सुनने की जाँच भी करवाएँ।',
    ],
    who: ['स्पीच और लैंग्वेज थेरेपिस्ट', 'ऑडियोलॉजिस्ट (सुनने की जाँच)', DEV_PAED],
    nurture:
      'घरेलू आकलन आपके बच्चे की उम्र के हिसाब से रोज़मर्रा की बातचीत के बारे में पूछता है, और रिपोर्ट दिखाती है कि बोलना और समझना कहाँ तक पहुँचा है, साथ में घर पर आज़माने के लिए गतिविधियाँ और स्पीच थेरेपिस्ट या डॉक्टर के लिए एक पेज।',
    sources: [
      { label: 'ASHA; Speech and language development', href: 'https://www.asha.org/public/speech/development/' },
      AAP('ages-stages/toddler/Pages/Language-Delay.aspx', 'Language delay'),
    ],
  },

  'developmental-delay': {
    intro: [
      '**विकास में देरी (डेवलपमेंटल डिले)** का मतलब है कि किसी छोटे बच्चे के कई पड़ाव, बैठना, चलना, बोलना, खेलना, ख़ुद खाना, ज़्यादातर बच्चों से देर से पूरे हो रहे हैं। यह बच्चे की आज की स्थिति बताता है, उनका भविष्य नहीं।',
      '**बौद्धिक अक्षमता (इंटेलेक्चुअल डिसेबिलिटी)** शब्द तब इस्तेमाल होता है जब सीखना और रोज़मर्रा के कौशल लंबे समय तक काफ़ी पीछे रहते हैं; आमतौर पर इसकी पुष्टि 5 साल की उम्र के बाद होती है। बौद्धिक अक्षमता वाले बच्चे, जिनमें डाउन सिंड्रोम वाले बच्चे भी शामिल हैं, पूरी ज़िंदगी सीखते और बढ़ते रहते हैं, ख़ासकर जब उन्हें जल्दी सहारा मिले।',
    ],
    notice: [
      'एक से ज़्यादा क्षेत्रों में, उसी उम्र के दूसरे बच्चों की तुलना में पड़ाव साफ़ तौर पर देर से आना।',
      'बड़े होने के साथ खाने, कपड़े पहनने या शौच जाने में हमउम्र बच्चों से ज़्यादा मदद की ज़रूरत पड़ना।',
      'निर्देश समझने, दिखावे वाले खेल (जैसे गुड़िया को खाना खिलाना) खेलने, या खेल में दूसरों के साथ चल पाने में ज़्यादा मुश्किल होना।',
    ],
    helps: [
      'रोज़मर्रा के कौशलों को बहुत छोटे-छोटे चरणों में बाँटें और एक बार में एक चरण का अभ्यास करें, कई बार, हर बार एक ही क्रम में।',
      'दिनचर्या (खाना, नहाना, कपड़े पहनना) को सीखने का समय बनाएँ, ये तो वैसे भी रोज़ होते हैं।',
      'पहले करके दिखाएँ, फिर मदद करें, फिर पीछे हट जाएँ: साथ मिलकर करें, फिर आख़िरी हिस्सा उन्हें ख़ुद करने दें।',
      'छोटी-छोटी प्रगति पर ध्यान दें और उसका जश्न मनाएँ। प्रगति धीमी हो सकती है, लेकिन वह असली है।',
    ],
    myths: [
      { myth: 'अगर देरी तो रहनी ही है, तो जल्दी मदद का कोई फ़ायदा नहीं।', fact: 'जल्दी मिला सहारा आत्मनिर्भरता और बातचीत में सच में फ़र्क लाता है। शुरुआत करने के लिए कभी भी बहुत जल्दी नहीं होती।' },
    ],
    when: [
      'आपका बच्चा दो या ज़्यादा क्षेत्रों में पीछे है, या उनके पहले सीखे हुए कौशल छूट गए हैं।',
      '3 साल से कम उम्र में कोई भी चिंता, इसी समय सहारा सबसे ज़्यादा मदद करता है।',
    ],
    who: [DEV_PAED, 'ऑक्युपेशनल थेरेपिस्ट', 'फ़िज़ियोथेरेपिस्ट', 'स्पीच और लैंग्वेज थेरेपिस्ट', 'स्पेशल एजुकेटर', 'आपका नज़दीकी ज़िला अर्ली इंटरवेंशन सेंटर (DEIC)'],
    nurture:
      'पड़ावों की जाँच आपके बच्चे की उम्र के हिसाब से चलने-फिरने, बातचीत, खेल और आत्मनिर्भरता को देखती है। रिपोर्ट दिखाती है कि किन क्षेत्रों में पड़ाव पूरे हो रहे हैं और किन पर ध्यान देने की ज़रूरत है, साथ में घर की हर जगह के लिए गतिविधियाँ।',
    sources: [
      AAP('health-issues/conditions/developmental-disabilities/Pages/default.aspx', 'Developmental disabilities'),
      NHS('conditions/learning-disabilities/', 'Learning disabilities'),
      RBSK,
    ],
  },

  'sensory-differences': {
    intro: [
      'बच्चे दुनिया को अपनी इंद्रियों से समझते हैं, आवाज़, छूना, स्वाद, गंध, देखना, हिलना-डुलना और शरीर की स्थिति का एहसास। कुछ बच्चों को कुछ एहसास दूसरों से **कहीं ज़्यादा तेज़** महसूस होते हैं (कमीज़ का टैग रेगमाल जैसा चुभता है), और कुछ को **कहीं कम** (वे चीज़ों से टकराते हैं, गोल-गोल घूमते हैं और ज़ोरदार दबाव ढूँढते हैं)।',
      'संवेदी अंतर ऑटिस्टिक बच्चों और ADHD वाले बच्चों में आम हैं, लेकिन अपने आप में अलग से भी हो सकते हैं।',
    ],
    notice: [
      'मिक्सर-ग्राइंडर, प्रेशर कुकर या भीड़ में कान ढक लेना।',
      'कुछ कपड़े पहनने, बाल धुलवाने, नाख़ून कटवाने, या मिले-जुले टेक्सचर वाले खाने से मना करना।',
      'लगातार हिलते रहना, कूदना, घूमना या चीज़ें चबाना।',
      'चोट लगने, गंदे होने या पुकारे जाने पर भी जैसे पता ही न चलना।',
      'शादियों, मॉल या बाज़ार जैसी भीड़-भाड़ और शोर वाली जगहों पर बेकाबू हो जाना (मेल्टडाउन)।',
    ],
    helps: [
      'पैटर्न पर ध्यान दें: मुश्किल पल से ठीक पहले क्या हुआ था? जो ट्रिगर मिलें, उनके हिसाब से योजना बनाएँ।',
      'हल्की रोशनी और तकिये या कंबल के साथ एक शांत कोना दें, और हालात बिगड़ने से पहले ही उन्हें वहाँ जाने दें।',
      'हिलने-डुलने के ब्रेक दें, कूदना, भारी थैले उठाना, कपड़ों की टोकरी धकेलना, ख़ासकर उन कामों से पहले जिनमें बैठना पड़ता है।',
      'तेज़ आवाज़ों से पहले बता दें, और शोर वाली जगहों पर उन्हें ईयर डिफ़ेंडर या हेडफ़ोन पहनने दें।',
      'नए टेक्सचर और खाने धीरे-धीरे, बिना दबाव के शुरू करें।',
    ],
    when: ['संवेदी प्रतिक्रियाओं की वजह से आपके बच्चे का ठीक से खाना, सोना या रोज़मर्रा की ज़िंदगी में शामिल होना रुक जाता है।', 'मेल्टडाउन अक्सर होते हैं या बहुत तीव्र होते हैं।'],
    who: ['ऑक्युपेशनल थेरेपिस्ट', DEV_PAED],
    nurture:
      'मुश्किल पलों को, उनसे पहले क्या हुआ था उसके साथ, लिखते रहें, पैटर्न जल्दी दिखने लगते हैं। घरेलू प्रोग्राम में शांत कोने और हिलने-डुलने वाली गतिविधियाँ हैं, जिन्हें आप दिन में शामिल कर सकते हैं।',
    sources: [AAP('health-issues/conditions/developmental-disabilities/Pages/Sensory-Integration-Therapy.aspx', 'Sensory integration')],
  },

  'coordination-difficulties': {
    intro: [
      'डेवलपमेंटल कोऑर्डिनेशन डिसऑर्डर (DCD), जिसे कभी-कभी डिस्प्रैक्सिया भी कहते हैं, का मतलब है कि बच्चे को हरकतों की योजना बनाने और उनमें तालमेल बिठाने में दूसरों से कहीं ज़्यादा मुश्किल होती है, बटन और लिखावट से लेकर गेंद पकड़ने या साइकिल चलाने तक। यह कमज़ोरी या कम बुद्धि की वजह से नहीं होता।',
    ],
    notice: [
      'अनाड़ी लगना: चीज़ों से टकराना, अक्सर गिरना, चीज़ें गिरा देना।',
      'हमउम्र बच्चों के सीख जाने के काफ़ी बाद भी बटन, ज़िप, फीते, चम्मच-काँटे या कैंची में दिक्कत।',
      'धीमी, थकाने वाली या मुश्किल से पढ़ी जाने वाली लिखावट।',
      'खेलकूद और मैदान के खेलों से बचना; अलग-थलग पड़ जाना।',
    ],
    helps: [
      'छोटे, मज़ेदार सत्रों में एक बार में एक ही कौशल का अभ्यास करें, हफ़्ते में एक बार एक घंटे से रोज़ कुछ मिनट बेहतर हैं।',
      'कौशल को चरणों में बाँटें और आख़िरी चरण का अभ्यास पहले करें, ताकि वे हर बार कामयाबी के साथ ख़त्म करें।',
      'जब तक कौशल बन रहे हैं, इलास्टिक कमर और वेल्क्रो वाले कपड़े चुनें।',
      'ताक़त और संतुलन बढ़ाने वाली गतिविधियाँ आज़माएँ: तैरना, चढ़ना, सामान उठाना, बड़ी नरम गेंद से कैच-कैच खेलना।',
    ],
    when: ['तालमेल की दिक्कतें अपनी देखभाल, स्कूल के काम या खेल पर असर डाल रही हैं, और सुधर नहीं रहीं।', 'आपके बच्चे का आत्मविश्वास कम हो रहा है या वे गतिविधियों से बच रहे हैं।'],
    who: ['ऑक्युपेशनल थेरेपिस्ट', 'फ़िज़ियोथेरेपिस्ट', DEV_PAED],
    nurture:
      'घरेलू आकलन के पड़ाव और आत्मनिर्भरता वाले सवाल रोज़मर्रा की हरकतों और अपनी देखभाल को कवर करते हैं; घरेलू प्रोग्राम में हाथों और पूरे शरीर की हरकतों के लिए गतिविधियाँ हैं।',
    sources: [NHS('conditions/developmental-coordination-disorder-dyspraxia/', 'Developmental coordination disorder')],
  },

  anxiety: {
    intro: [
      'सभी बच्चे कभी-कभी चिंतित या डरे हुए महसूस करते हैं, अँधेरे से, नई जगहों से, आपसे दूर होने से। चिंता (एंग्ज़ायटी) तब समस्या बनती है जब चिंताएँ बड़ी हों, बार-बार हों, और बच्चे को वे काम करने से रोकें जो उसकी उम्र के दूसरे बच्चे कर सकते हैं।',
      'एंग्ज़ायटी का इलाज अच्छी तरह हो सकता है, और इस मदद में माता-पिता की बड़ी भूमिका होती है।',
    ],
    notice: [
      'स्कूल, बाहर जाने या दूसरों के पास छोड़े जाने से पहले चिपके रहना, रोना या पेट दर्द।',
      '“अगर ऐसा हुआ तो?” वाले बहुत सारे सवाल, और बार-बार भरोसा दिलाने की ज़रूरत, जिसका असर ज़्यादा देर नहीं टिकता।',
      'उन चीज़ों से बचना जो पहले पसंद थीं, या नई स्थितियों से बचना।',
      'नींद आने में मुश्किल, बुरे सपने, या आपके साथ सोने की ज़िद।',
      'चिड़चिड़ापन या मेल्टडाउन, जिनके पीछे असल में डर होता है।',
    ],
    helps: [
      'शांति से भावना का नाम लें: “तुम्हें कल की चिंता है। यह समझ में आता है।”',
      'डर से पूरी तरह बचने के बजाय, छोटे-छोटे क़दमों में उसका सामना करने में उनकी मदद करें, बचते रहने से चिंता बढ़ती है।',
      'रोज़ की दिनचर्या स्थिर और नींद नियमित रखें।',
      'जब वे शांत हों, तब शांत होने का एक तरीक़ा सिखाएँ, धीमी “फूल सूँघो, मोमबत्ती बुझाओ” वाली साँस।',
      'अपनी चिंता पर भी ध्यान दें; बच्चे हमें देखकर सीखते हैं कि डर को कैसे सँभालना है।',
    ],
    when: [
      'चिंता की वजह से आपके बच्चे का स्कूल जाना, सोना, खाना या दोस्तों से मिलना रुक गया है।',
      'यह कुछ हफ़्तों से ज़्यादा समय से चल रहा है, या आपको उनकी सुरक्षा की चिंता है।',
    ],
    who: ['बाल मनोवैज्ञानिक (चाइल्ड साइकोलॉजिस्ट)', 'बाल मनोचिकित्सक (चाइल्ड साइकियाट्रिस्ट)', 'स्कूल काउंसलर'],
    nurture:
      'किशोरों के घरेलू आकलन में मूड और चिंता से जुड़े सवाल होते हैं, और जब किसी बात पर ध्यान देने की ज़रूरत हो तो रिपोर्ट साफ़-साफ़ बताती है। अगर आपका बच्चा कभी ख़ुद को नुक़सान पहुँचाने की बात करे, तो तुरंत Tele-MANAS को **14416** पर कॉल करें।',
    sources: [NHS('mental-health/children-and-young-adults/advice-for-parents/anxiety-in-children/', 'Anxiety in children'), AAP('health-issues/conditions/emotional-problems/Pages/Anxiety-Disorders.aspx', 'Anxiety disorders')],
  },
};
